import { useCallback, useMemo, useState } from 'react';
import { Breadcrumb, Empty } from 'antd';
import { Link } from 'react-router-dom';
import type { SorterResult } from 'antd/es/table/interface';

import { sendNotification, transformFiltersV2 } from 'utils/helperMethods';
import {
    TPOGEN_BREADCRUMB_CLICKED,
    TPOGEN_CONSTRAINT_LIBRARY_EXPORTED,
    TPOGEN_CONSTRAINT_ROLLBACK,
    TPOGEN_CONSTRAINT_UPDATED,
} from 'utils/mixpanelEventNames';

import { useTpoGenConfig } from '../context/CustomerConfigContext';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../utils/trackTpoGenEvent';
import { isPromoLibraryKpiColumn } from '../config/promoLibraryConfig';
import type { PromoLibraryKpiColumnConfig } from '../config/promoLibraryConfig';
import PromoLibraryToolbar from './PromoLibraryToolbar';
import PromoLibraryTable from './PromoLibraryTable';
import EditKpiModal from './components/EditKpiModal';
import type { EditKpiPayload } from './components/EditKpiModal';
import ResetKpisModal from './components/ResetKpisModal';
import type { ResetKpisPayload } from './components/ResetKpisModal';
import usePromoLibraryData from './hooks/queries/usePromoLibraryData';
import useUpdatePromoLibraryKpiMutation from './hooks/mutations/useUpdatePromoLibraryKpiMutation';
import useResetPromoLibraryKpisMutation from './hooks/mutations/useResetPromoLibraryKpisMutation';
import useExportPromoLibraryMutation from './hooks/mutations/useExportPromoLibraryMutation';
import usePromoLibraryColumns from './hooks/ui/usePromoLibraryColumns';
import usePromoLibraryUrlState from './hooks/ui/usePromoLibraryUrlState';
import { formatRecordCount } from './utils/formatPromoLibraryValue';
import type { EditKpiTarget, FiltersPayload, PromoLibraryRow } from './types';

// ---------------------------------------------------------------------------
// Validation helpers (mirroring the legacy PromoLibraryContainer guards)
// ---------------------------------------------------------------------------

/** Maximum rows the backend will export in a single request. */
const EXPORT_LIMIT = 10_000;

/**
 * Ensures exactly one customer (dim2) value is included before a destructive
 * operation (reset or bulk edit). Mirrors `validateSingleCustomerSelection`
 * from the legacy `tpoHelperMethods`.
 */
const validateCustomerFilter = (filters: FiltersPayload | null | undefined): boolean => {
    const dim2 = filters?.['dim2'];
    const inclusionCount = dim2?.inclusion?.length ?? 0;
    const exclusionCount = dim2?.exclusion?.length ?? 0;

    if (exclusionCount > 0 || inclusionCount > 1) {
        sendNotification('error', 'Please select only one customer');
        return false;
    }
    if (inclusionCount === 0) {
        sendNotification('error', 'Please select at least one customer');
        return false;
    }
    return true;
};

const PromoLibrary = () => {
    // 1. Context / store hooks
    const tpoGenConfig = useTpoGenConfig();
    const { promoLibraryConfig, fieldValueLabelMaps, labels } = tpoGenConfig;

    // 2. State
    const [editTarget, setEditTarget] = useState<EditKpiTarget | null>(null);
    const [resetModalOpen, setResetModalOpen] = useState(false);

    // 3. UI hook — drives URL-backed view state; must precede query params derivation
    const {
        searchKeyword,
        setSearchKeyword,
        showChange,
        setShowChange,
        selectedKpis,
        setSelectedKpis,
        sortBy,
        sortOrder,
        setSort,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        setAppliedFilters,
        resetFilters,
    } = usePromoLibraryUrlState(promoLibraryConfig);

    // 4. Derived values that feed the query
    const kpiColumns = useMemo<PromoLibraryKpiColumnConfig[]>(
        () => (promoLibraryConfig?.columns ?? []).filter(isPromoLibraryKpiColumn),
        [promoLibraryConfig],
    );

    const visibleColumns = useMemo(
        () =>
            (promoLibraryConfig?.columns ?? []).filter(
                (column) => column.kind === 'attribute' || selectedKpis.includes(column.key),
            ),
        [promoLibraryConfig, selectedKpis],
    );

    const attributeFields = useMemo(
        () => visibleColumns.filter((column) => column.kind === 'attribute').map((column) => column.key),
        [visibleColumns],
    );

    const visibleKpiColumns = useMemo(() => visibleColumns.filter(isPromoLibraryKpiColumn), [visibleColumns]);

    const kpiFields = useMemo(() => visibleKpiColumns.map((column) => column.key), [visibleKpiColumns]);

    const filters = useMemo<FiltersPayload>(
        () => transformFiltersV2(applyFilterObject, promoLibraryConfig?.filterKeys ?? []) as FiltersPayload,
        [applyFilterObject, promoLibraryConfig],
    );

    // Guarded against a stale `sortBy` from the URL naming a column this customer
    // no longer configures.
    const sortField = useMemo(
        () => ((promoLibraryConfig?.columns ?? []).some((column) => column.key === sortBy) ? sortBy : ''),
        [promoLibraryConfig, sortBy],
    );

    // 5. Query hook
    const {
        dataSource,
        isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        totalCount,
        loadedCount,
    } = usePromoLibraryData({
        libraryGroup: promoLibraryConfig?.libraryGroup ?? '',
        attributes: attributeFields,
        kpis: kpiFields,
        filters,
        searchKeyword,
        sortBy: sortField,
        sortOrder,
        pageSize: promoLibraryConfig?.pageSize ?? 100,
    });

    // 6. Mutation hooks
    // `constraint_type`/`scope_level` (TPOGEN-023) have no dedicated API fields, so
    // constraint_type is derived from the edited KPI key and scope_level from the
    // attribute columns that define this row's granularity — the closest fe_derived
    // stand-ins for the CSV spec's fe_enum properties.
    const { mutate: updateKpi, isPending: isUpdatingKpi } = useUpdatePromoLibraryKpiMutation({
        onSuccess: (_data, variables) => {
            trackTpoGenEvent(TPOGEN_CONSTRAINT_UPDATED, TPOGEN_PAGE_NAMES.PROMO_LIBRARY, {
                constraint_type: variables.kpi_key,
                scope_level: attributeFields.length ? attributeFields.join('+') : 'row',
            });
            setEditTarget(null);
        },
    });
    const { mutate: resetKpis, isPending: isResettingKpis } = useResetPromoLibraryKpisMutation({
        onSuccess: () => setResetModalOpen(false),
    });
    const { mutate: exportLibrary, isPending: isExporting } = useExportPromoLibraryMutation();

    // 7. Remaining derived values
    const editableKpiColumns = useMemo(
        () => kpiColumns.filter((col) => col.editConfig),
        [kpiColumns],
    );

    const activeEditConfig = useMemo(() => {
        if (!editTarget) return undefined;
        const col = kpiColumns.find((c) => c.key === editTarget.kpiKey);
        return col?.editConfig;
    }, [editTarget, kpiColumns]);

    const hasActiveFilters = Object.keys(applyFilterObject).length > 0;

    // 8. Columns UI hook (depends on derived values above; handleEdit must precede it)
    const handleEdit = useCallback((target: EditKpiTarget) => setEditTarget(target), []);

    const columns = usePromoLibraryColumns({
        columns: visibleColumns,
        fieldValueLabelMaps,
        showChange,
        sortBy,
        sortOrder,
        onEdit: handleEdit,
    });

    // 9. Event handlers
    const handleEditCancel = useCallback(() => setEditTarget(null), []);

    const handleEditSubmit = useCallback(
        (payload: EditKpiPayload) => {
            if (!validateCustomerFilter(filters)) return;
            updateKpi({
                kpi_key: payload.kpiKey,
                row_id: payload.rowId,
                value: payload.value as number | string | boolean,
            });
        },
        [updateKpi, filters],
    );

    const handleResetSubmit = useCallback(
        (payload: ResetKpisPayload) => {
            // No versioning system backs the Promo Library today, so `from_version`/
            // `to_version` (TPOGEN-024) use fixed labels describing this reset-to-default
            // action rather than real version ids.
            trackTpoGenEvent(TPOGEN_CONSTRAINT_ROLLBACK, TPOGEN_PAGE_NAMES.PROMO_LIBRARY, {
                from_version: 'current',
                to_version: payload.resetOption === 'all' ? 'default_all' : 'default_filtered',
                kpi_count: payload.kpiKeys.length,
            });
            resetKpis({
                kpi_keys: payload.kpiKeys,
                reset_option: payload.resetOption,
                filters,
            });
        },
        [resetKpis, filters],
    );

    const handleExport = useCallback(() => {
        if (totalCount > EXPORT_LIMIT) {
            sendNotification(
                'error',
                `The data exceeds the export limit of ${EXPORT_LIMIT.toLocaleString()} records. Please apply more filters to reduce the data before proceeding with the export.`,
            );
            return;
        }
        trackTpoGenEvent(TPOGEN_CONSTRAINT_LIBRARY_EXPORTED, TPOGEN_PAGE_NAMES.PROMO_LIBRARY, {
            format: 'csv',
            row_count: totalCount,
        });
        exportLibrary({
            attributes: attributeFields,
            kpis: kpiFields,
            filters,
            search_string: searchKeyword,
            sort_by: sortField,
            sorting_order: sortOrder,
            limit: EXPORT_LIMIT,
            offset: 0,
        });
    }, [exportLibrary, totalCount, attributeFields, kpiFields, filters, searchKeyword, sortField, sortOrder]);

    const handleAction = useCallback(
        (key: string) => {
            if (key === 'export') {
                handleExport();
            } else if (key === 'reset_promo_library' || key === 'reset_library') {
                if (!validateCustomerFilter(filters)) return;
                setResetModalOpen(true);
            }
        },
        [handleExport, filters],
    );

    const handleTableChange = useCallback(
        (
            _pagination: unknown,
            _filters: unknown,
            sorter: SorterResult<PromoLibraryRow> | SorterResult<PromoLibraryRow>[],
        ) => {
            const single = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSortBy = (single?.columnKey ?? single?.field) as string | undefined;
            if (!single?.order || !nextSortBy) {
                setSort('', 'asc');
                return;
            }
            setSort(nextSortBy, single.order === 'ascend' ? 'asc' : 'desc');
        },
        [setSort],
    );

    return (
        <div>
            <div className="mb-3">
                <Breadcrumb>
                    <Breadcrumb.Item>
                        <Link
                            to="/tpo"
                            onClick={() =>
                                trackTpoGenEvent(TPOGEN_BREADCRUMB_CLICKED, TPOGEN_PAGE_NAMES.PROMO_LIBRARY, {
                                    source_page: 'promo_library',
                                    target_page: 'tpo_home',
                                    object_type: 'promo',
                                })
                            }
                        >
                            {labels.common.breadcrumbRoot}
                        </Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{promoLibraryConfig?.labels.breadcrumb ?? 'Promo Library'}</Breadcrumb.Item>
                </Breadcrumb>
            </div>

            <div className="flex flex-col rounded-[0.25rem] border-[1] border-[#EAEAEC] bg-[#FFF] px-[1.5rem] py-[1.75rem]">
                {promoLibraryConfig ? (
                    <>
                        <PromoLibraryToolbar
                            config={promoLibraryConfig}
                            kpiColumns={kpiColumns}
                            selectedKpis={selectedKpis}
                            setSelectedKpis={setSelectedKpis}
                            showChange={showChange}
                            setShowChange={setShowChange}
                            allCheckedAttribute={allCheckedAttribute}
                            allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                            applyFilterObject={applyFilterObject}
                            setAppliedFilters={setAppliedFilters}
                            resetFilters={resetFilters}
                            isExporting={isExporting}
                            onAction={handleAction}
                        />

                        <PromoLibraryTable
                            dataSource={dataSource}
                            columns={columns}
                            isPending={isPending}
                            isFetching={isFetching}
                            isFetchingNextPage={isFetchingNextPage}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            onChange={handleTableChange}
                            emptyDescription={promoLibraryConfig.labels.noData}
                            emptySubtitle={promoLibraryConfig.labels.noDataSubtitle}
                        />

                        <div className="mt-3 flex justify-end">
                            <p className="text-xs text-[#807E86]">
                                {formatRecordCount(promoLibraryConfig.labels.recordCount, loadedCount, totalCount)}
                            </p>
                        </div>

                        <EditKpiModal
                            open={!!editTarget}
                            target={editTarget}
                            editConfig={activeEditConfig}
                            loading={isUpdatingKpi}
                            onSubmit={handleEditSubmit}
                            onCancel={handleEditCancel}
                        />

                        <ResetKpisModal
                            open={resetModalOpen}
                            editableKpis={editableKpiColumns}
                            loading={isResettingKpis}
                            hasActiveFilters={hasActiveFilters}
                            onSubmit={handleResetSubmit}
                            onCancel={() => setResetModalOpen(false)}
                        />
                    </>
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Promo Library is not configured for this customer."
                    />
                )}
            </div>
        </div>
    );
};

PromoLibrary.displayName = 'PromoLibrary';

export default PromoLibrary;
